import { z } from 'zod';
import { VollWalletMoneyInputSchema } from './voll_wallet_money.js';

/** O status de um pedido de saldo, como a lista o mostra. `other` = um status do fluxo que a tela
 *  não nomeia (erro, aguardando resultado). */
export const VollWalletRequestStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'canceled',
  'other',
]);
export type VollWalletRequestStatus = z.infer<typeof VollWalletRequestStatusSchema>;

/** Quem aprova um pedido de saldo, com a decisão que vale para o status do pedido: os de um pedido
 *  em aprovação ainda não decidiram, o de um aprovado aprovou, o de um recusado recusou. */
export const VollWalletRequestApproverSchema = z
  .object({
    name: z.string().min(1),
    /** O dia da decisão (no fuso de São Paulo); ausente enquanto o pedido espera. */
    date: z.iso.date().optional(),
    /** A justificativa da recusa. */
    justification: z.string().optional(),
  })
  .strict();
export type VollWalletRequestApprover = z.infer<typeof VollWalletRequestApproverSchema>;

/** Um pedido de saldo do viajante ("Meus pedidos"). */
export const VollWalletBalanceRequestItemSchema = z
  .object({
    id: z.string().min(1),
    code: z.string().nullable(),
    status: VollWalletRequestStatusSchema,
    description: z.string().nullable(),
    period: z.object({ start: z.iso.date(), end: z.iso.date() }).strict().nullable(),
    /** As carteiras do pedido: o id (para o acréscimo), o nome, a diária, se o valor é livre e o
     *  valor DESTA carteira (`amount`; opcional para o BFF e a tela poderem subir em qualquer ordem). */
    wallets: z.array(
      z
        .object({
          id: z.string().min(1),
          name: z.string(),
          dailyRate: z.number().nonnegative(),
          flexible: z.boolean(),
          amount: z.number().optional(),
        })
        .strict(),
    ),
    amounts: z.array(
      z.object({ amount: z.number(), currency: z.string().length(3).optional() }).strict(),
    ),
    /** Aprovado e vigente, sem acréscimo vigente: aceita "Mais saldo ou prazo". */
    topUp: z.boolean(),
    /** Em aprovação ou aprovado, enquanto o período não terminou (no dia de São Paulo): o
     *  request-flow-api aceita o cancelamento (`created`, `pending`, `approved`), mas o aprovado
     *  encerrado já liberou o saldo (#6768) e o pendente encerrado não tem mais o que liberar
     *  (#6887). Cancelado e recusado nunca mostram a ação. O padrão `false` deixa a tela subir
     *  antes do BFF: sem o campo, só some a ação, a lista continua a abrir. */
    cancelable: z.boolean().default(false),
    /** O dia do pedido (no fuso de São Paulo). Este campo e os seguintes são opcionais para o BFF
     *  e a tela poderem subir em qualquer ordem (#6768). */
    requestedAt: z.iso.date().optional(),
    /** A alocação do pedido: os nomes da empresa, do centro de custo e do motivo, e a observação. */
    company: z.string().optional(),
    costCenter: z.string().optional(),
    motive: z.string().optional(),
    observation: z.string().optional(),
    /** Com quem o pedido está ou quem o decidiu; vazio quando o fluxo não diz. */
    approvers: z.array(VollWalletRequestApproverSchema).optional(),
  })
  .strict();
export type VollWalletBalanceRequestItem = z.infer<typeof VollWalletBalanceRequestItemSchema>;

/** `GET /voll-wallet/balance/requests` — os pedidos de saldo do viajante, os mais recentes antes. */
export const VollWalletBalanceRequestsSchema = z
  .object({ requests: z.array(VollWalletBalanceRequestItemSchema) })
  .strict();
export type VollWalletBalanceRequests = z.infer<typeof VollWalletBalanceRequestsSchema>;

/**
 * `POST /voll-wallet/balance/top-up` — "Mais saldo ou prazo" numa carteira de um pedido aprovado
 * e vigente: uma nova data final (o BFF cobra a diária dos dias a mais) e/ou, numa carteira
 * flexível, um valor a mais. A alocação e o motivo seguem os do pedido.
 */
export const VollWalletTopUpRequestSchema = z
  .object({
    parentId: z.string().min(1),
    walletId: z.string().min(1),
    end: z.iso.date().optional(),
    amount: VollWalletMoneyInputSchema.optional(),
    description: z.string().trim().min(1).max(300),
  })
  .strict()
  .refine((r) => r.end !== undefined || r.amount !== undefined, {
    message: 'end or amount',
  });
export type VollWalletTopUpRequest = z.infer<typeof VollWalletTopUpRequestSchema>;

/** As recusas que a tela do acréscimo explica. */
export const VollWalletTopUpErrorSchema = z.discriminatedUnion('error', [
  z.object({ error: z.literal('request_unavailable') }).strict(),
  z.object({ error: z.literal('period_invalid') }).strict(),
  z.object({ error: z.literal('amount_missing') }).strict(),
  z.object({ error: z.literal('balance_rejected') }).strict(),
  /** O termo da política da wallet ainda não foi aceito. */
  z.object({ error: z.literal('policy_pending') }).strict(),
  /** O wallets-api falhou sem recusar: não dá para afirmar que nada foi feito (#5688). */
  z.object({ error: z.literal('outcome_unknown') }).strict(),
]);
export type VollWalletTopUpError = z.infer<typeof VollWalletTopUpErrorSchema>;

/** `POST /voll-wallet/balance/requests/:id/cancel` — as recusas que a tela explica. */
export const VollWalletCancelErrorSchema = z.discriminatedUnion('error', [
  /** O pedido não está na lista do viajante ou já não aceita cancelamento. */
  z.object({ error: z.literal('request_unavailable') }).strict(),
  z.object({ error: z.literal('cancel_rejected') }).strict(),
  /** O request-flow-api falhou sem recusar: não dá para afirmar que nada foi feito. */
  z.object({ error: z.literal('outcome_unknown') }).strict(),
]);
export type VollWalletCancelError = z.infer<typeof VollWalletCancelErrorSchema>;
